






































































































































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import HhAuditService from "@/services/HhAuditService";

type ChecklistItem = {
  blok: string;
  item_kode: string;
  item_urutan: number;
  item_teks: string;
  hasil: string | null;
  keterangan: string | null;
};

type Tally = { ya: number; tidak: number; skor: number };

@Component
export default class HhAuditChecklistForm extends Vue {
  @Prop({ required: true }) readonly sessionId!: number;

  session: any = null;
  saving = false;
  signing = false;
  submitDialog = false;
  password = "";

  // Katalog dari server (single source of truth teks item)
  blokOptions: string[] = [];
  blokLabels: Record<string, string> = {};
  kelompokFasilitas: string[] = [];
  kelompokKepatuhan: string[] = [];
  ambangBaik = 85;
  ambangCukup = 75;

  items: ChecklistItem[] = [];
  openPanels: number[] = [0, 1, 2, 3];

  sarana: any = {
    jumlah_wastafel: null,
    jumlah_handrub: null,
    jumlah_box_tisu: null
  };
  waktuAudit = "";
  rekomendasi = "";

  get locked(): boolean {
    return !!(this.session && this.session.is_locked);
  }

  get jenisAudit(): string {
    return (this.session && this.session.jenis_audit) || "gabungan";
  }

  get formTitle(): string {
    if (this.jenisAudit === "fasilitas") return "Audit Fasilitas Hand Hygiene";
    if (this.jenisAudit === "kepatuhan") return "Audit Kepatuhan Hand Hygiene";
    return "Audit Hand Hygiene";
  }

  /**
   * Blok yang ditampilkan sesuai jenis form. Blok fasilitas (A) sudah dipindah
   * ke form terpisah "Audit Fasilitas Hand Hygiene", jadi hanya sesi ber-jenis
   * "fasilitas" yang menampilkannya. Sesi lama "gabungan" diperlakukan sebagai
   * audit kepatuhan (blok B/C/D saja).
   */
  get visibleBloks(): string[] {
    if (this.jenisAudit === "fasilitas") return this.kelompokFasilitas;
    return this.kelompokKepatuhan;
  }

  get hasFasilitas(): boolean {
    return this.visibleBloks.some(b => this.kelompokFasilitas.indexOf(b) >= 0);
  }

  get hasKepatuhan(): boolean {
    return this.visibleBloks.some(b => this.kelompokKepatuhan.indexOf(b) >= 0);
  }

  itemsOf(blok: string): ChecklistItem[] {
    return this.items.filter(i => i.blok === blok);
  }

  tally(bloks: string[]): Tally {
    const scoped = this.items.filter(i => bloks.indexOf(i.blok) >= 0);
    const ya = scoped.filter(i => i.hasil === "ya").length;
    const tidak = scoped.filter(i => i.hasil === "tidak").length;
    const den = ya + tidak;
    return { ya, tidak, skor: den > 0 ? (ya / den) * 100 : 0 };
  }

  blokTally(blok: string): Tally {
    return this.tally([blok]);
  }

  get liveFasilitas(): Tally {
    return this.tally(this.kelompokFasilitas);
  }

  get liveKepatuhan(): Tally {
    return this.tally(this.kelompokKepatuhan);
  }

  get liveTotal(): Tally {
    return this.tally(this.visibleBloks);
  }

  get liveKategori(): string {
    const t = this.liveTotal;
    if (t.ya + t.tidak === 0) return "";
    if (t.skor >= this.ambangBaik) return "baik";
    if (t.skor >= this.ambangCukup) return "cukup";
    return "kurang";
  }

  get needsRekomendasi(): boolean {
    return this.liveKategori === "kurang";
  }

  get canSubmit(): boolean {
    if (this.locked) return false;
    if (this.liveTotal.ya + this.liveTotal.tidak === 0) return false;
    if (this.needsRekomendasi && !this.rekomendasi) return false;
    return true;
  }

  kategoriColor(k: string): string {
    if (k === "baik") return "success";
    if (k === "cukup") return "warning";
    if (k === "kurang") return "error";
    return "grey";
  }

  kategoriLabel(k: string): string {
    if (k === "baik") return "BAIK";
    if (k === "cukup") return "CUKUP";
    if (k === "kurang") return "KURANG";
    return "—";
  }

  formatDate(s: string) {
    if (!s) return "-";
    const d = new Date(s);
    return d.toLocaleDateString("id-ID");
  }

  async mounted() {
    await this.loadCatalog();
    await this.loadSession();
  }

  async loadCatalog() {
    const res = await HhAuditService.catalog();
    const c = res.data.data;
    this.blokOptions = c.blok_options;
    this.blokLabels = c.blok_labels;
    this.kelompokFasilitas = c.kelompok_fasilitas;
    this.kelompokKepatuhan = c.kelompok_kepatuhan;
    this.ambangBaik = c.ambang.baik;
    this.ambangCukup = c.ambang.cukup;

    // Kerangka item dari katalog (nilai diisi dari sesi)
    this.items = c.items.map((i: any) => ({
      blok: i.blok,
      item_kode: i.item_kode,
      item_urutan: i.item_urutan,
      item_teks: i.item_teks,
      hasil: null,
      keterangan: null
    }));
  }

  async loadSession() {
    const res = await HhAuditService.find(this.sessionId);
    const data = res.data.data;
    this.session = data;

    this.rekomendasi = data.rekomendasi_tindak_lanjut || "";
    this.sarana = {
      jumlah_wastafel: data.jumlah_wastafel,
      jumlah_handrub: data.jumlah_handrub,
      jumlah_box_tisu: data.jumlah_box_tisu
    };
    this.waktuAudit = (data.waktu_audit || "").slice(0, 5);

    (data.items || []).forEach((saved: any) => {
      const target = this.items.find(i => i.item_kode === saved.item_kode);
      if (target) {
        target.hasil = saved.hasil;
        target.keterangan = saved.keterangan;
      }
    });
  }

  async saveDraft() {
    this.saving = true;
    try {
      await HhAuditService.update(this.sessionId, {
        unit_id: this.session.unit_id,
        // kirim Y-m-d saja — mencegah pergeseran hari saat round-trip
        tanggal_audit: String(this.session.tanggal_audit || "").slice(0, 10),
        waktu_audit: this.waktuAudit || null,
        periode_bulan: this.session.periode_bulan,
        periode_tahun: this.session.periode_tahun,
        jumlah_wastafel: this.sarana.jumlah_wastafel,
        jumlah_handrub: this.sarana.jumlah_handrub,
        jumlah_box_tisu: this.sarana.jumlah_box_tisu,
        rekomendasi_tindak_lanjut: this.rekomendasi
      });

      await HhAuditService.saveItems(
        this.sessionId,
        this.items
          .filter(i => this.visibleBloks.indexOf(i.blok) >= 0)
          .map(i => ({
            item_kode: i.item_kode,
            hasil: i.hasil,
            keterangan: i.keterangan
          }))
      );

      await this.loadSession();
    } finally {
      this.saving = false;
    }
  }

  openSubmit() {
    if (!this.canSubmit) return;
    this.submitDialog = true;
    this.password = "";
  }

  async submitSign() {
    if (!this.password) return;
    this.signing = true;
    try {
      await this.saveDraft();
      await HhAuditService.submitAuditor(this.sessionId, { password: this.password });
      this.submitDialog = false;
      this.$emit("submitted");
    } finally {
      this.signing = false;
    }
  }
}
